import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { useGetCpDatabaseQuery } from "../../../store/appApi";
import { downloadCpWorkbook } from "../../../services/cpDatabase";
import { selectUserRole } from "../../../store/authSlice";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const CpDatabaseList = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching } = useGetCpDatabaseQuery();
  const role = useSelector(selectUserRole);
  const isSuperAdmin = role === "super-admin";

  const [employeeId, setEmployeeId] = useState("");
  const [cpNameFilter, setCpNameFilter] = useState("");
  const [cpPhoneFilter, setCpPhoneFilter] = useState("");

  const allCps = useMemo(
    () => (Array.isArray(data?.cps) ? data.cps : []),
    [data],
  );
  // Unique employees who added CPs, for the super-admin filter.
  const employees = useMemo(() => {
    const map = new Map();
    allCps.forEach((c) => {
      const id = String(c.addedBy || "");
      if (id && !map.has(id)) map.set(id, c.addedByName || "Unknown");
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allCps]);
  // Employee + CP name + CP phone (phone matches on digits only).
  const nameNeedle = cpNameFilter.trim().toLowerCase();
  const phoneNeedle = cpPhoneFilter.replace(/\D/g, "");
  const hasFilter = !!(employeeId || nameNeedle || phoneNeedle);
  const cps = allCps.filter(
    (c) =>
      (!employeeId || String(c.addedBy) === employeeId) &&
      (!nameNeedle || String(c.cpName || "").toLowerCase().includes(nameNeedle)) &&
      (!phoneNeedle || String(c.phone || "").replace(/\D/g, "").includes(phoneNeedle)),
  );
  const loading = isLoading || isFetching;

  return (
    <>
      <PageTitle activeMenu="CP Database" motherMenu="Workforce" />

      <div className="space-y-6">
        <section className={`${panelClass} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                CP Database
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                Channel Partners
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                {isSuperAdmin
                  ? "Every channel partner added by any team member."
                  : "Channel partners you've added."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400"
                  onClick={() => navigate("/cp-database/add")}
                >
                  + Add CP
                </button>
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
                  onClick={() =>
                    downloadCpWorkbook({ addedBy: employeeId || undefined })
                  }
                  disabled={cps.length === 0}
                >
                  Download Excel
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {cps.length} CP{cps.length === 1 ? "" : "s"}
            </h2>
            <div className="flex flex-wrap items-center gap-3">
            <input
              type="search"
              className="w-48 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-amber-500 focus:outline-none"
              placeholder="CP name"
              value={cpNameFilter}
              onChange={(e) => setCpNameFilter(e.target.value)}
              aria-label="Filter by CP name"
            />
            <input
              type="search"
              inputMode="tel"
              className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-amber-500 focus:outline-none"
              placeholder="CP phone"
              value={cpPhoneFilter}
              onChange={(e) => setCpPhoneFilter(e.target.value)}
              aria-label="Filter by CP phone"
            />
            {isSuperAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-600">
                Employee
                <select
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-amber-500 focus:outline-none"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                >
                  <option value="">All employees</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    CP Name
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Company Name
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Address
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Remarks
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Added By
                  </th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wide text-[11px]">
                    Added On
                  </th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wide text-[11px]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : cps.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-12 text-center text-gray-400"
                    >
                      {hasFilter
                        ? "No CP matches these filters."
                        : "No CP entries yet."}
                    </td>
                  </tr>
                ) : (
                  cps.map((c) => (
                    <tr key={c._id} className="hover:bg-amber-50/30">
                      <td
                        className="px-4 py-3 font-medium text-gray-900"
                        style={{ textTransform: "uppercase" }}
                      >
                        {c.cpName}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{c.phone}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.companyName || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.address || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-xs">
                        {c.remarks || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {c.addedByName}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {c.createdAt
                          ? new Date(c.createdAt).toLocaleDateString("en-IN", {
                              dateStyle: "medium",
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="rounded-full border border-amber-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-700 transition hover:bg-amber-50 hover:text-amber-700"
                          onClick={() => navigate(`/cp-database/edit/${c._id}`)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default CpDatabaseList;
